// ============================================================================
// Which buttons a given role may see on a given payout row (CW-N 3b).
//
// This is a MIRROR of incentive_payouts_enforce_verifier_scope() and the
// stamping trigger, both read from the migrations applied 2026-08-13. The
// database is the authority: if a button rendered here throws, the gating in
// this file is wrong, not the DB. Kept as pure data so it can be tested
// without mounting a component.
//
// Transitions the gate actually permits:
//
//   account_manager   pending_review -> am_validated | disqualified
//                     am_validated   -> pending_review | disqualified
//                     disqualified   -> am_validated
//                     compliment only: verified|sent_to_finance -> paid
//
//   national_am       pending_review -> am_validated | verified | disqualified
//                     am_validated   -> verified | disqualified | pending_review
//                     disqualified   -> am_validated | verified
//                     verified       -> sent_to_finance | am_validated | pending_review
//                     compliment only: verified|sent_to_finance -> paid
//
//   finance           no_misses only: verified|sent_to_finance -> paid
//
//   superuser/executive  everything
// ============================================================================

import { canMarkIncentivePaid, canValidateIncentive, hasFullAmAuthority } from '../../shared/role-gates';
import type {
  IncentivePayoutStatus,
  IncentivePayoutType,
  UserRole,
} from '../../shared/types';

/** What the viewer is allowed to attempt, derived once from their role. */
export interface IncentiveCaps {
  /** Step 1 — validate / send back / disqualify own rows. */
  canValidate: boolean;
  /** Step 2 — approve, unapprove, route to finance. */
  canApprove: boolean;
  /** Mark a No-Miss paid (payroll). */
  canMarkPaid: boolean;
  /** Mark a Compliment gift card given. */
  canMarkGiven: boolean;
  /** Generate a period from the queue. */
  canGenerate: boolean;
}


export function capsForRole(role: UserRole | null | undefined): IncentiveCaps {
  const isApprover = hasFullAmAuthority(role);
  const isAm = canValidateIncentive(role);
  return {
    canValidate: isAm,
    canApprove: isApprover,
    // The stamping trigger restricts a No-Miss "paid" to finance, superuser
    // and executive — a national_am may NOT mark payroll paid.
    canMarkPaid: canMarkIncentivePaid(role),
    canMarkGiven: isAm,
    canGenerate: isApprover,
  };
}

export type ActionKind =
  | 'validate'
  | 'approve'
  | 'send_back'
  | 'unapprove'
  | 'send_to_finance'
  | 'mark_paid'
  | 'mark_given'
  | 'disqualify'
  | 'reinstate';

export interface PayoutAction {
  kind: ActionKind;
  label: string;
  target: IncentivePayoutStatus;
  variant: 'primary' | 'ghost';
  /** Needs the reason dialog before it can be submitted. */
  needsReason?: boolean;
}

export function actionsFor(
  status: IncentivePayoutStatus,
  payoutType: IncentivePayoutType,
  caps: IncentiveCaps,
): PayoutAction[] {
  const isNmi = payoutType === 'no_misses_monthly';
  const out: PayoutAction[] = [];

  if (status === 'pending_review') {
    if (caps.canValidate)
      out.push({ kind: 'validate', label: 'Validate', target: 'am_validated', variant: 'primary' });
    if (caps.canApprove)
      out.push({ kind: 'approve', label: 'Approve', target: 'verified', variant: 'primary' });
    if (caps.canValidate)
      out.push({
        kind: 'disqualify',
        label: 'Disqualify…',
        target: 'disqualified',
        variant: 'ghost',
        needsReason: true,
      });
    return out;
  }

  if (status === 'am_validated') {
    if (caps.canApprove)
      out.push({ kind: 'approve', label: 'Approve', target: 'verified', variant: 'primary' });
    if (caps.canValidate)
      out.push({
        kind: 'send_back',
        label: 'Send back',
        target: 'pending_review',
        variant: 'ghost',
      });
    if (caps.canValidate)
      out.push({
        kind: 'disqualify',
        label: 'Disqualify…',
        target: 'disqualified',
        variant: 'ghost',
        needsReason: true,
      });
    return out;
  }

  if (status === 'verified') {
    if (isNmi) {
      // CW-Q 4.4. Finance could not reach "Mark paid" from an approved No-Miss
      // row at all: this branch only ever offered actions to an APPROVER, so a
      // verified row was a dead end for Jose and the only way forward was for
      // someone else to route it to finance first. The DB has always permitted
      // it — `incentive_payouts_enforce_verifier_scope()` returns NEW for
      // role='finance' when `old IN ('verified','sent_to_finance')` and
      // `new = 'paid'`, verified by probe inside a rolled-back transaction on
      // 2026-08-14. This is the missing affordance, not a policy change.
      if (caps.canMarkPaid) {
        out.push({
          kind: 'mark_paid',
          label: 'Mark paid',
          target: 'paid',
          variant: 'primary',
        });
      }
      if (caps.canApprove) {
        out.push({
          kind: 'send_to_finance',
          label: 'Send to finance',
          target: 'sent_to_finance',
          variant: caps.canMarkPaid ? 'ghost' : 'primary',
        });
        out.push({
          kind: 'unapprove',
          label: 'Unapprove',
          target: 'am_validated',
          variant: 'ghost',
        });
      }
    } else {
      if (caps.canMarkGiven)
        out.push({ kind: 'mark_given', label: 'Mark given', target: 'paid', variant: 'primary' });
      if (caps.canApprove)
        out.push({
          kind: 'unapprove',
          label: 'Unapprove',
          target: 'am_validated',
          variant: 'ghost',
        });
    }
    return out;
  }

  if (status === 'sent_to_finance') {
    if (isNmi) {
      if (caps.canMarkPaid)
        out.push({ kind: 'mark_paid', label: 'Mark paid', target: 'paid', variant: 'primary' });
    } else if (caps.canMarkGiven) {
      out.push({ kind: 'mark_given', label: 'Mark given', target: 'paid', variant: 'primary' });
    }
    return out;
  }

  if (status === 'disqualified') {
    // An approver reinstates straight to approved; an AM can only put it back
    // into their own validated state for the approver to look at.
    if (caps.canApprove)
      out.push({
        kind: 'reinstate',
        label: 'Reinstate…',
        target: 'verified',
        variant: 'primary',
        needsReason: true,
      });
    else if (caps.canValidate)
      out.push({
        kind: 'reinstate',
        label: 'Reinstate…',
        target: 'am_validated',
        variant: 'primary',
        needsReason: true,
      });
    return out;
  }

  // 'paid' is terminal — read-only stamp, no actions for anyone.
  return out;
}

// ---------------------------------------------------------------------------
// Stamp wording.
//
// The DB deliberately does NOT clear am_validated_by_eid / verified_by_eid on
// a send-back or an unapprove — the audit trail is the point. But rendering a
// bare "Validated by Melanie Lipchitz" on a row whose pill says "Pending
// review" reads as a contradiction, and an AM would reasonably conclude the
// screen is broken. Observed live on 2026-08-13: validate then send back, and
// the row claims both states at once.
//
// So the stamp stays, and the verb says which tense it is in.

/** Is the AM validation stamp describing the row's CURRENT state? */
export function validationIsCurrent(status: IncentivePayoutStatus): boolean {
  return status !== 'pending_review' && status !== 'disqualified';
}

/** Is the approver stamp describing the row's CURRENT state? */
export function approvalIsCurrent(status: IncentivePayoutStatus): boolean {
  return (
    status === 'verified' || status === 'sent_to_finance' || status === 'paid'
  );
}

export function validatedVerb(status: IncentivePayoutStatus): string {
  return validationIsCurrent(status) ? 'Validated' : 'Previously validated';
}

export function approvedVerb(status: IncentivePayoutStatus): string {
  return approvalIsCurrent(status) ? 'Approved' : 'Previously approved';
}

// ---------------------------------------------------------------------------
// Reason prefill (CW-N 3c, Ben's ruling).
//
// The reason for a disqualification is always the same, so the AM does not
// compose it. The DB REQUIRES a non-empty override_justification on any
// transition into 'disqualified', and on any transition OUT of an
// engine-disqualified row — so these strings must never be empty.

export const DISQUALIFY_REASON = 'Poor cleaning outcome.';

export function reinstateReason(periodLabel: string): string {
  return `Good cleaning outcome for ${periodLabel}.`;
}

export function withNote(base: string, note: string): string {
  const n = note.trim();
  return n ? `${base} ${n}` : base;
}

/**
 * True when the engine has already written the reason onto the record, in
 * which case disqualifying needs no dialog — the reason is on the row.
 * Either a linked piece of feedback, or a partial-period note written by the
 * generator ("Partial month — sub started 2026-07-20").
 */
export function hasEngineReason(row: {
  disqualifying_feedback_id: string | null;
  notes: string | null;
}): boolean {
  if (row.disqualifying_feedback_id) return true;
  return !!row.notes && /partial (month|quarter)/i.test(row.notes);
}

/** The justification to send when the engine already stated the reason. */
export function engineReason(row: {
  disqualifying_feedback_desc: string | null;
  disqualifying_feedback_date: string | null;
  notes: string | null;
}): string {
  if (row.disqualifying_feedback_desc) {
    const when = row.disqualifying_feedback_date
      ? ` (${row.disqualifying_feedback_date})`
      : '';
    return withNote(
      DISQUALIFY_REASON,
      `Feedback on record${when}: ${row.disqualifying_feedback_desc}`,
    );
  }
  const partial = row.notes
    ?.split('\n')
    .find((l) => /partial (month|quarter)/i.test(l))
    ?.trim();
  return partial ? withNote(DISQUALIFY_REASON, partial) : DISQUALIFY_REASON;
}
